/**
 * Time + SLA + validity-window helpers for the investigator workbench and entity-360.
 *
 * Dense, glanceable, deterministic. No wall-clock is hashed anywhere; `now` is injectable so the
 * pure formatters are testable and re-render-stable when a caller pins a reference instant.
 */

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

/** Parse an RFC-3339 instant to epoch ms, or null when unparseable/empty. */
export function toMs(iso?: string | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

/** Humanise a duration (ms, non-negative) compactly: "2h", "3d", "45m", "30s". */
export function humanizeDuration(ms: number): string {
  const abs = Math.abs(ms);
  if (abs < MIN) return `${Math.max(1, Math.round(abs / 1000))}s`;
  if (abs < HOUR) return `${Math.round(abs / MIN)}m`;
  if (abs < DAY) {
    const h = Math.floor(abs / HOUR);
    const m = Math.round((abs % HOUR) / MIN);
    return m ? `${h}h ${m}m` : `${h}h`;
  }
  const d = Math.floor(abs / DAY);
  const h = Math.round((abs % DAY) / HOUR);
  return h ? `${d}d ${h}h` : `${d}d`;
}

/** Signed relative phrase from `iso` to `now`: "in 2h", "3d ago", "just now". */
export function formatRelative(iso?: string | null, now: number = Date.now()): string {
  const t = toMs(iso);
  if (t == null) return '—';
  const delta = t - now;
  if (Math.abs(delta) < 45_000) return 'just now';
  return delta > 0 ? `in ${humanizeDuration(delta)}` : `${humanizeDuration(delta)} ago`;
}

/** Absolute UTC stamp for tooltips/audit: "2026-07-16 14:30 UTC". */
export function formatAbsolute(iso?: string | null): string {
  const t = toMs(iso);
  if (t == null) return '—';
  const d = new Date(t);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())} UTC`;
}

/** Short date for timeline groupings: "16 Jul 2026". */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function formatDay(iso?: string | null): string {
  const t = toMs(iso);
  if (t == null) return '—';
  const d = new Date(t);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

export type SlaLevel = 'none' | 'ok' | 'at-risk' | 'overdue';

export interface SlaState {
  level: SlaLevel;
  /** ms remaining until due (negative = past due). */
  remainingMs: number | null;
  /** Fraction of the SLA window still remaining (0..1), when a window is known. */
  fractionRemaining: number | null;
  /** Glanceable phrase: "due in 2h" / "overdue 40m" / "—". */
  label: string;
}

/**
 * SLA posture. `at-risk` when < 25% of the (created → due) window remains; `overdue` past due.
 * Without `createdAt` we fall back to an absolute 2h at-risk threshold so a badge still colours.
 */
export function slaState(
  slaDueAt?: string | null,
  createdAt?: string | null,
  now: number = Date.now(),
): SlaState {
  const due = toMs(slaDueAt);
  if (due == null) {
    return { level: 'none', remainingMs: null, fractionRemaining: null, label: '—' };
  }
  const remainingMs = due - now;
  if (remainingMs <= 0) {
    return {
      level: 'overdue',
      remainingMs,
      fractionRemaining: 0,
      label: `overdue ${humanizeDuration(remainingMs)}`,
    };
  }
  const created = toMs(createdAt);
  let atRisk: boolean;
  let fractionRemaining: number | null = null;
  if (created != null && due > created) {
    fractionRemaining = remainingMs / (due - created);
    atRisk = fractionRemaining < 0.25;
  } else {
    atRisk = remainingMs < 2 * HOUR;
  }
  return {
    level: atRisk ? 'at-risk' : 'ok',
    remainingMs,
    fractionRemaining,
    label: `due in ${humanizeDuration(remainingMs)}`,
  };
}

export type ValidityLevel = 'active' | 'expiring' | 'expired' | 'future' | 'unknown';

/**
 * Evidence validity posture at `at` (default now). `expiring` when the window closes within 30d.
 * `future` when not yet valid at the read coordinate (matters under a point-in-time view).
 */
export function validityState(
  validFrom?: string | null,
  validTo?: string | null,
  at: number = Date.now(),
): ValidityLevel {
  const from = toMs(validFrom);
  const to = toMs(validTo);
  if (from == null && to == null) return 'unknown';
  if (from != null && at < from) return 'future';
  if (to != null && at > to) return 'expired';
  if (to != null && to - at < 30 * DAY) return 'expiring';
  return 'active';
}
